<script setup lang="ts">
import PageContainer from "~/components/ui/PageContainer.vue";
import { buildComputerCollectionSchema } from "~/features/computer/data/seo/computer-schema";

const sections = [
  { title: "Practice Mode Long Questions", to: "/11th/computer/practice/long-questions/", description: "Mixed syllabus long questions ko unit filters ke sath revise karo." },
  { title: "SDLC Long Questions", to: "/11th/computer/sdlc/long-questions/", description: "SDLC stages, diagrams aur process ke long answers." },
  { title: "Python Long Questions", to: "/11th/computer/python/long-questions/", description: "Variables, conditions, loops aur functions ke long answers." },
  { title: "Algorithms Long Questions", to: "/11th/computer/algorithm/long-questions/", description: "Bubble sort aur algorithm explanation ke long answers." },
  { title: "Data Structures Long Questions", to: "/11th/computer/data-structures/long-questions/", description: "Stack, queue, tree aur graph ke long answers." },
  { title: "Data Analytics Long Questions", to: "/11th/computer/data-analytics/long-questions/", description: "Models, charts aur experiments ke long answers." },
  { title: "Digital Literacy Long Questions", to: "/11th/computer/digital-literacy/long-questions/", description: "Research process aur digital literacy ke long answers." },
  { title: "Entrepreneurship Long Questions", to: "/11th/computer/entrepreneurship/long-questions/", description: "Design thinking, business plan aur marketing ke long answers." },
];

const supportLinks = [
  { title: "Pairing Scheme", to: "/11th/computer/pairing-scheme/", description: "Fixed long-question chapters samajhne ke liye." },
  { title: "Guess Paper", to: "/11th/computer/guess-paper/", description: "Most expected long questions dekhne ke liye." },
  { title: "Practice Mode", to: "/11th/computer/practice/long-questions/", description: "Structured answer revision ke liye." },
];

const faqs = [
  {
    question: "11th class computer long questions kin chapters se aate hain?",
    answer:
      "Long questions zyada tar fixed chapters se aate hain. Python, Algorithms, Data Structures aur Data Analytics sab se important long-question chapters hote hain.",
  },
  {
    question: "1st year computer long questions ka best prep route kya hai?",
    answer:
      "Pairing scheme se fixed chapters samjhein, phir chapter-wise long questions kholo aur aakhir me guess paper ya practice mode se revision karein.",
  },
  {
    question: "Long questions hub aur guess paper me kya difference hai?",
    answer:
      "Long questions hub chapter-wise complete prep ke liye hai, jab ke guess paper final expected sawalat ko filter karke dikhata hai.",
  },
];

usePageSeo({
  title: "11th Class Computer Long Questions | Ustaad",
  description: "11th class computer long questions ke liye chapter-wise hub: Python, SDLC, Algorithms aur baqi chapters ke long answers ek jagah.",
  keywords: "11th class computer long questions, first year computer long questions, chapter wise computer long questions",
  faqs,
  breadcrumbs: [
    { name: "Home", item: "/" },
    { name: "11th Computer", item: "/11th/computer/" },
    { name: "Long Questions", item: "/11th/computer/long-questions/" },
  ],
  schemaType: "CollectionPage",
  schemaProperties: buildComputerCollectionSchema({
    about: "11th Class Computer Long Questions Hub",
    items: sections.map((section) => ({ name: section.title, path: section.to })),
  }),
});
</script>

<template>
  <PageContainer>
    <section class="rounded-[30px] border border-slate-200 bg-gradient-to-br from-sky-50 via-white to-amber-50 px-5 py-6 shadow-sm dark:border-slate-800 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950 md:px-8 md:py-8">
      <p class="text-xs font-semibold uppercase tracking-[0.24em] text-sky-700">Long Questions Hub</p>
      <h1 class="mt-3 text-3xl font-bold tracking-tight text-slate-950 dark:text-white md:text-5xl">11th Class Computer Long Questions</h1>
      <p class="mt-4 max-w-3xl text-sm leading-7 text-slate-700 dark:text-slate-300">
        Yahan se chapter-wise long questions kholo. Agar aapko descriptive prep karni ho, yeh page direct long-answer routes tak le jata hai.
      </p>
      <p class="mt-3 max-w-3xl text-sm leading-7 text-slate-600 dark:text-slate-300">
        Long questions fixed chapters se aate hain, is liye yeh page chapter-wise descriptive prep ko simple route me organize karta hai.
      </p>
    </section>

    <section class="rounded-[28px] border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900 md:p-6">
      <div>
        <p class="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">Start Here</p>
        <h2 class="mt-2 text-2xl font-semibold tracking-tight text-slate-950 dark:text-white">Choose your chapter</h2>
      </div>
      <div class="mt-5 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        <NuxtLink
          v-for="section in sections"
          :key="section.to"
          :to="section.to"
          class="rounded-[22px] border border-slate-200 bg-slate-50 p-5 transition hover:border-sky-300 hover:bg-white dark:border-slate-800 dark:bg-slate-950 dark:hover:border-sky-700 dark:hover:bg-slate-900"
        >
          <h3 class="text-lg font-semibold text-slate-950 dark:text-white">{{ section.title }}</h3>
          <p class="mt-2 text-sm leading-7 text-slate-600 dark:text-slate-300">{{ section.description }}</p>
        </NuxtLink>
      </div>
    </section>

    <section class="rounded-[28px] border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900 md:p-6">
      <div>
        <p class="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">Related Prep</p>
        <h2 class="mt-2 text-2xl font-semibold tracking-tight text-slate-950 dark:text-white">Use long questions with chapter mapping</h2>
      </div>
      <div class="mt-5 grid gap-4 md:grid-cols-3">
        <NuxtLink
          v-for="item in supportLinks"
          :key="item.to"
          :to="item.to"
          class="rounded-[22px] border border-slate-200 bg-slate-50 p-5 transition hover:border-sky-300 hover:bg-white dark:border-slate-800 dark:bg-slate-950 dark:hover:border-sky-700 dark:hover:bg-slate-900"
        >
          <h3 class="text-lg font-semibold text-slate-950 dark:text-white">{{ item.title }}</h3>
          <p class="mt-2 text-sm leading-7 text-slate-600 dark:text-slate-300">{{ item.description }}</p>
        </NuxtLink>
      </div>
    </section>

    <section class="rounded-[28px] border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900 md:p-6">
      <div>
        <p class="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">FAQ</p>
        <h2 class="mt-2 text-2xl font-semibold tracking-tight text-slate-950 dark:text-white">11th Computer Long Questions FAQs</h2>
      </div>
      <div class="mt-5 space-y-4">
        <article
          v-for="faq in faqs"
          :key="faq.question"
          class="rounded-[22px] border border-slate-200 bg-slate-50 p-5 dark:border-slate-800 dark:bg-slate-950"
        >
          <h3 class="text-lg font-semibold text-slate-950 dark:text-white">{{ faq.question }}</h3>
          <p class="mt-2 text-sm leading-7 text-slate-600 dark:text-slate-300">{{ faq.answer }}</p>
        </article>
      </div>
    </section>
  </PageContainer>
</template>
